import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

// Public (unauthenticated) axios instance — no auth interceptor.
// The main api.js instance adds Authorization only when a token exists,
// but to be explicit we use a dedicated instance here.
const publicApi = axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    headers: { 'Content-Type': 'application/json' },
});

// Cloudflare Turnstile always-pass dev site key.
// Set REACT_APP_TURNSTILE_SITE_KEY in prod build args to the real site key
// from dash.cloudflare.com → Turnstile → site detail.
const TURNSTILE_SITE_KEY =
    process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const INITIAL_FORM = {
    org_number: '',
    company_name: '',
    address: '',
    contact_person: '',
    contact_email: '',
    employee_count: '',
    message: '',
};

function FieldError({ error }) {
    if (!error) return null;
    return <p className="mt-1 text-xs text-destructive">{error}</p>;
}

export default function LeadForm() {
    const { t } = useTranslation();
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [turnstileToken, setTurnstileToken] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);

    // Render the Turnstile widget after the component mounts.
    // The script is loaded async in index.html, so we poll until window.turnstile
    // is available (usually within a few hundred ms).
    useEffect(() => {
        let attempts = 0;
        const MAX_ATTEMPTS = 40; // 40 × 250ms = 10s max wait

        const tryRender = () => {
            if (
                turnstileRef.current &&
                window.turnstile &&
                widgetIdRef.current === null
            ) {
                widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => setTurnstileToken(token),
                    'expired-callback': () => setTurnstileToken(''),
                    'error-callback': () => setTurnstileToken(''),
                    theme: 'auto',
                });
                return;
            }
            attempts += 1;
            if (attempts < MAX_ATTEMPTS) {
                setTimeout(tryRender, 250);
            }
        };

        tryRender();

        return () => {
            if (widgetIdRef.current !== null && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (_) {
                    // ignore if widget already gone
                }
                widgetIdRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetTurnstile = useCallback(() => {
        setTurnstileToken('');
        if (widgetIdRef.current !== null && window.turnstile) {
            try {
                window.turnstile.reset(widgetIdRef.current);
            } catch (_) {
                // ignore
            }
        }
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        // Clear per-field error on change
        setErrors((prev) => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    }, []);

    const validate = () => {
        const errs = {};

        if (!form.org_number.trim()) {
            errs.org_number = t('lead.form.field.org_number.required');
        } else if (
            form.org_number.trim().length < 10 ||
            form.org_number.trim().length > 13
        ) {
            errs.org_number = t('lead.form.field.org_number.format');
        }

        if (!form.company_name.trim()) {
            errs.company_name = t('lead.form.field.company_name.required');
        }

        if (!form.address.trim()) {
            errs.address = t('lead.form.field.address.required');
        }

        if (!form.contact_person.trim()) {
            errs.contact_person = t('lead.form.field.contact_person.required');
        }

        if (!form.contact_email.trim()) {
            errs.contact_email = t('lead.form.field.contact_email.required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())) {
            errs.contact_email = t('lead.form.field.contact_email.format');
        }

        const count = parseInt(form.employee_count, 10);
        if (!form.employee_count) {
            errs.employee_count = t('lead.form.field.employee_count.required');
        } else if (Number.isNaN(count) || count < 1) {
            errs.employee_count = t('lead.form.field.employee_count.format');
        }

        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        if (!turnstileToken) {
            toast.error(t('error.leads.captcha_failed'));
            return;
        }

        setSubmitting(true);

        try {
            await publicApi.post('/leads/request-access', {
                org_number: form.org_number.trim(),
                company_name: form.company_name.trim(),
                address: form.address.trim(),
                contact_person: form.contact_person.trim(),
                contact_email: form.contact_email.trim().toLowerCase(),
                employee_count: parseInt(form.employee_count, 10),
                message: form.message.trim(),
                turnstile_token: turnstileToken,
            });

            setSubmitted(true);
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;

            if (status === 400 && detail === 'error.leads.captcha_failed') {
                toast.error(t('error.leads.captcha_failed'));
            } else if (status === 429 && detail === 'error.leads.rate_limited') {
                toast.error(t('error.leads.rate_limited'));
            } else if (status === 409 && detail === 'error.leads.duplicate_submission') {
                toast.error(t('error.leads.duplicate_submission'));
            } else if (status === 503) {
                toast.error(t('error.leads.captcha_unavailable'));
            } else if (status === 422) {
                toast.error(t('error.leads.validation'));
            } else {
                toast.error(t('error.leads.network'));
            }

            resetTurnstile();
        } finally {
            setSubmitting(false);
        }
    };

    const allRequiredFilled =
        form.org_number.trim() &&
        form.company_name.trim() &&
        form.address.trim() &&
        form.contact_person.trim() &&
        form.contact_email.trim() &&
        form.employee_count;

    const canSubmit = allRequiredFilled && turnstileToken && !submitting;

    // ── Success state ────────────────────────────────────────────────────
    if (submitted) {
        return (
            <div className="py-8 text-center space-y-4">
                <CheckCircle2
                    className="mx-auto size-10 text-foreground"
                    aria-hidden="true"
                />
                <h2 className="font-heading text-xl font-bold uppercase tracking-wider">
                    {t('lead.form.success.title')}
                </h2>
                <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                    {t('lead.form.success.body')}
                </p>
            </div>
        );
    }

    // ── Form ─────────────────────────────────────────────────────────────
    return (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
            <div>
                <h2 className="font-heading text-xl font-bold uppercase tracking-wider">
                    {t('lead.form.title')}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                    {t('lead.form.subtitle')}
                </p>
            </div>

            {/* Two-column grid on sm+ */}
            <div className="grid gap-4 sm:grid-cols-2">
                {/* org_number */}
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="org_number">
                        {t('lead.form.field.org_number.label')}
                        <span className="text-destructive ml-1" aria-hidden="true">*</span>
                    </Label>
                    <Input
                        id="org_number"
                        name="org_number"
                        value={form.org_number}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.org_number.placeholder')}
                        autoComplete="off"
                        aria-required="true"
                        aria-invalid={!!errors.org_number}
                    />
                    <FieldError error={errors.org_number} />
                </div>

                {/* company_name */}
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="company_name">
                        {t('lead.form.field.company_name.label')}
                        <span className="text-destructive ml-1" aria-hidden="true">*</span>
                    </Label>
                    <Input
                        id="company_name"
                        name="company_name"
                        value={form.company_name}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.company_name.placeholder')}
                        autoComplete="organization"
                        aria-required="true"
                        aria-invalid={!!errors.company_name}
                    />
                    <FieldError error={errors.company_name} />
                </div>

                {/* address — full width */}
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label htmlFor="address">
                        {t('lead.form.field.address.label')}
                        <span className="text-destructive ml-1" aria-hidden="true">*</span>
                    </Label>
                    <Input
                        id="address"
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.address.placeholder')}
                        autoComplete="street-address"
                        aria-required="true"
                        aria-invalid={!!errors.address}
                    />
                    <FieldError error={errors.address} />
                </div>

                {/* contact_person */}
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="contact_person">
                        {t('lead.form.field.contact_person.label')}
                        <span className="text-destructive ml-1" aria-hidden="true">*</span>
                    </Label>
                    <Input
                        id="contact_person"
                        name="contact_person"
                        value={form.contact_person}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.contact_person.placeholder')}
                        autoComplete="name"
                        aria-required="true"
                        aria-invalid={!!errors.contact_person}
                    />
                    <FieldError error={errors.contact_person} />
                </div>

                {/* contact_email */}
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="contact_email">
                        {t('lead.form.field.contact_email.label')}
                        <span className="text-destructive ml-1" aria-hidden="true">*</span>
                    </Label>
                    <Input
                        id="contact_email"
                        name="contact_email"
                        type="email"
                        value={form.contact_email}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.contact_email.placeholder')}
                        autoComplete="email"
                        aria-required="true"
                        aria-invalid={!!errors.contact_email}
                    />
                    <FieldError error={errors.contact_email} />
                </div>

                {/* employee_count */}
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="employee_count">
                        {t('lead.form.field.employee_count.label')}
                        <span className="text-destructive ml-1" aria-hidden="true">*</span>
                    </Label>
                    <Input
                        id="employee_count"
                        name="employee_count"
                        type="number"
                        min="1"
                        value={form.employee_count}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.employee_count.placeholder')}
                        aria-required="true"
                        aria-invalid={!!errors.employee_count}
                    />
                    <FieldError error={errors.employee_count} />
                </div>

                {/* message — full width, optional */}
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <Label htmlFor="message">
                        {t('lead.form.field.message.label')}
                        <span className="text-muted-foreground ml-1 text-xs">
                            ({t('common.optional')})
                        </span>
                    </Label>
                    <Textarea
                        id="message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder={t('lead.form.field.message.placeholder')}
                        rows={4}
                        maxLength={2000}
                    />
                </div>
            </div>

            {/* Turnstile widget */}
            <div ref={turnstileRef} className="cf-turnstile" />

            {/* Submit */}
            <div className="space-y-3">
                <Button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full sm:w-auto uppercase tracking-widest text-sm font-semibold"
                >
                    {submitting ? t('lead.form.submitting') : t('lead.form.submit')}
                </Button>

                <p className="text-xs text-muted-foreground">
                    {t('lead.form.consent')}
                </p>
            </div>
        </form>
    );
}
